import React, { useState, useEffect, useRef } from 'react';
import { 
  Clock, 
  Zap, 
  Shield, 
  TrendingUp, 
  FileText, 
  Users, 
  MessageSquare, 
  Scale,
  Search,
  DollarSign,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Play,
  Menu,
  X,
  ArrowRight,
  Plug,
  Laptop,
  ClipboardCheck,
  Star,
  Building2,
  Briefcase,
  UserCircle,
  Phone,
  Video,
  Calendar,
  BookOpen,
  Gavel,
  CreditCard,
  Lock,
  Award,
  Quote
} from 'lucide-react';
import * as Logos from './components/logos';

// Animation hook for scroll reveal
const useScrollReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Animated section wrapper
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-slate-800 tracking-tight">TimeFlow</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <a href="#how-it-works" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">How It Works</a>
            <a href="#benefits" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">Benefits</a>
            <a href="#integrations" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">Integrations</a>
            <a href="#testimonials" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">Testimonials</a>
            <a href="#faq" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">FAQ</a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <button className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors px-4 py-2">
              Sign In
            </button>
            <button className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-all hover:shadow-lg hover:shadow-indigo-500/25">
              Start Free Trial
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-100 py-4">
            <div className="flex flex-col gap-2">
              <a href="#how-it-works" className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">How It Works</a>
              <a href="#benefits" className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">Benefits</a>
              <a href="#integrations" className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">Integrations</a>
              <a href="#testimonials" className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">Testimonials</a>
              <a href="#faq" className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">FAQ</a>
              <div className="border-t border-slate-100 mt-2 pt-4 px-4">
                <button className="w-full bg-indigo-600 text-white font-medium py-3 rounded-lg">
                  Start Free Trial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative pt-20 lg:pt-32 pb-12 lg:pb-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-indigo-100/40 to-transparent" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl" />
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-indigo-200/30 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Tagline */}
            <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1.5 mb-5">
              <Zap className="w-4 h-4 text-indigo-600" />
              <span className="text-xs sm:text-sm font-medium text-indigo-700">Capture Every Billable Minute</span>
            </div>

            {/* Headline */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900 leading-tight mb-5 break-words">
              Stop Losing
              <span className="relative inline-block mt-1">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 break-words">Billable Hours</span>
                <svg className="absolute -bottom-1 left-0 w-full max-w-[200px]" viewBox="0 0 200 12" fill="none">
                  <path d="M2 10C50 4 150 4 198 10" stroke="url(#underline-gradient)" strokeWidth="3" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="underline-gradient" x1="0" y1="0" x2="200" y2="0">
                      <stop stopColor="#4F46E5" />
                      <stop offset="1" stopColor="#8B5CF6" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
              <span className="block mt-1">to Manual Time Entry</span>
            </h1>

            {/* Subhead */}
            <p className="text-base sm:text-lg text-slate-600 mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed break-words">
              TimeFlow connects to your legal software ecosystem and uses AI to reconstruct accurate time entries from your actual work—so you capture every billable minute without lifting a finger.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6">
              <button className="group bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-indigo-500/25 flex items-center justify-center gap-2">
                Start Free Trial
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="group bg-white hover:bg-slate-50 text-slate-700 font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-xl border border-slate-200 transition-all flex items-center justify-center gap-2">
                <Play className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                Watch Demo
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-center lg:justify-start text-xs sm:text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                <span className="hidden xs:inline">No credit card required</span>
                <span className="xs:hidden">No card required</span>
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                <span>14-day trial</span>
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                <span className="hidden sm:inline">Setup in 5 minutes</span>
                <span className="sm:hidden">5 min setup</span>
              </span>
            </div>
          </div>

          {/* Right Content - Product Screenshot */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-200/50 overflow-hidden">
              {/* Browser Chrome */}
              <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400" />
                </div>
                <div className="flex-1 mx-4">
                  <div className="bg-white rounded-md px-3 py-1.5 text-xs text-slate-400 text-center">
                    app.timeflow.legal
                  </div>
                </div>
              </div>
              
              {/* Dashboard Preview */}
              <div className="p-6 bg-slate-50">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-semibold text-slate-800">Today's Captured Time</h3>
                    <p className="text-sm text-slate-500">Wednesday, January 10</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-indigo-600">6.4h</div>
                    <p className="text-xs text-emerald-600 font-medium">+2.1h recovered</p>
                  </div>
                </div>

                {/* Time Entries */}
                <div className="space-y-3">
                  {[
                    { matter: 'Johnson v. Smith Corp', activity: 'Document Review - Contract Analysis', time: '1.8h', status: 'approved', icon: FileText },
                    { matter: 'Meridian Acquisition', activity: 'Legal Research - Westlaw', time: '1.2h', status: 'pending', icon: Search },
                    { matter: 'Chen Estate Planning', activity: 'Client Call - Teams Meeting', time: '0.9h', status: 'approved', icon: Phone },
                    { matter: 'TechStart Series A', activity: 'Email Correspondence', time: '0.6h', status: 'pending', icon: MessageSquare },
                  ].map((entry, i) => (
                    <div key={i} className="bg-white rounded-lg p-4 border border-slate-200 hover:border-indigo-200 hover:shadow-sm transition-all">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${entry.status === 'approved' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                          <entry.icon className={`w-4 h-4 ${entry.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-slate-800 text-sm truncate">{entry.matter}</p>
                            <span className="text-sm font-semibold text-slate-700">{entry.time}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{entry.activity}</p>
                        </div>
                        <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                          entry.status === 'approved' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {entry.status === 'approved' ? '✓ Approved' : 'Review'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Integration Status */}
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Connected Sources</span>
                    <div className="flex items-center gap-1">
                      {['iManage', 'Teams', 'Westlaw', 'Clio'].map((tool, i) => (
                        <div key={i} className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs font-medium">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {tool}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100 hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">This Month</p>
                  <p className="font-bold text-slate-800">$47,280 <span className="text-emerald-600 text-sm font-medium">recovered</span></p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100 hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Hours Saved Daily</p>
                  <p className="font-bold text-slate-800">45 min <span className="text-indigo-600 text-sm font-medium">per attorney</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Target Audience Section - Law Firms That Bill By The Hour
const TargetAudienceSection = () => {
  const lawFirmTypes = [
    'Large corporate law firms (Biglaw)',
    'Mid-sized corporate firms',
    'Intellectual property firms',
    'Labor and employment firms (defense side)',
    'Real estate law firms',
    'Tax law firms',
    'Immigration law firms',
    'Trusts, estates, and wealth transfer firms',
    'Commercial litigation firms',
    'Regulatory and compliance firms',
    'Securities law firms',
    'Bankruptcy and restructuring firms',
    'Franchise law firms',
    'Mergers and acquisitions firms',
    'Antitrust law firms',
    'Healthcare law firms',
    'Environmental law firms',
    'Insurance coverage law firms',
    'Construction law firms',
    'Admiralty and maritime law firms',
    'Government contracts law firms',
    'Data privacy and cybersecurity firms',
    'Capital markets firms',
    'Private equity firms',
    'Venture capital firms'
  ];

  return (
    <section className="py-16 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-12 px-4">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-4 break-words">
              This product is perfect if you are one of these law firms that does not get paid on contingency but on <span className="text-indigo-600">billable hours</span>.
            </h2>
          </div>
        </AnimatedSection>

        <div className="max-w-5xl mx-auto px-2 sm:px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4 sm:gap-x-6">
            {lawFirmTypes.map((firmType, i) => (
              <AnimatedSection key={i} delay={i * 25}>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all min-w-0">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-slate-700 text-sm font-medium break-words min-w-0">{firmType}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>

        <AnimatedSection delay={600}>
          <div className="text-center mt-10">
            <p className="text-slate-600 mb-4">If your firm bills by the hour, TimeFlow helps you capture every minute you deserve.</p>
            <button className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded-xl transition-all hover:shadow-lg hover:shadow-indigo-500/25 inline-flex items-center gap-2">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Social Proof Bar
const SocialProofBar = () => {
  const stats = [
    { value: '500+', label: 'Law Firms' },
    { value: '$2M+', label: 'Recovered Monthly' },
    { value: '98%', label: 'Renewal Rate' },
    { value: '4.8/5', label: 'Average Rating' },
  ];

  return (
    <section className="py-12 bg-white border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <p className="text-center text-sm font-medium text-slate-500 mb-8">
            Trusted by leading law firms and legal professionals
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-slate-800">{stat.value}</div>
                <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Logo Strip */}
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-60">
            {['Morrison & Chen LLP', 'Blackwell Partners', 'Sterling Law Group', 'Apex Legal', 'Whitmore Associates'].map((firm, i) => (
              <div key={i} className="flex items-center gap-2 text-slate-400">
                <Scale className="w-5 h-5" />
                <span className="font-semibold text-sm tracking-wide">{firm}</span>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Pain Points Section
const PainPointsSection = () => {
  const painPoints = [
    {
      icon: DollarSign,
      title: 'Lost Revenue',
      description: 'Studies show attorneys lose 10-40% of billable time to poor tracking. That\'s thousands in revenue slipping away monthly.',
      stat: '10-40%',
      statLabel: 'billable time lost'
    },
    {
      icon: Clock,
      title: 'Wasted Hours',
      description: 'Partners spend 30+ minutes daily reconstructing time entries. Time that could be spent on billable work or with family.',
      stat: '30+',
      statLabel: 'minutes wasted daily'
    },
    {
      icon: Laptop,
      title: 'Fragmented Work',
      description: 'Jumping between 8+ apps means activities slip through the cracks. Email, documents, research, calls—impossible to track manually.',
      stat: '8+',
      statLabel: 'apps to track'
    },
    {
      icon: Calendar,
      title: 'Billing Delays',
      description: 'End-of-month time entry marathons delay invoicing and cash flow. Clients question entries you can\'t fully recall.',
      stat: '2-3',
      statLabel: 'weeks delayed billing'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }} />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              The Hidden Cost of Manual Time Tracking
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Every day, valuable billable hours disappear because manual time tracking can't keep up with how lawyers actually work.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {painPoints.map((point, i) => (
            <AnimatedSection key={i} delay={i * 100}>
              <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-6 h-full hover:bg-slate-800/70 transition-colors group">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <point.icon className="w-6 h-6 text-red-400" />
                </div>
                <div className="mb-3">
                  <span className="text-3xl font-bold text-white">{point.stat}</span>
                  <span className="text-sm text-slate-500 ml-2">{point.statLabel}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{point.description}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={400}>
          <div className="text-center mt-12">
            <p className="text-xl text-indigo-400 font-medium">
              What if your time tracked itself?
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      icon: Plug,
      title: 'Connect Your Tools',
      description: 'Link TimeFlow to your existing legal software in minutes—iManage, Clio, Westlaw, Teams, and 15+ more. No IT department required.',
      visual: 'integrations'
    },
    {
      number: '02',
      icon: Laptop,
      title: 'Work Normally',
      description: 'TimeFlow runs silently in the background, analyzing your activity across all connected systems. No behavior change needed.',
      visual: 'working'
    },
    {
      number: '03',
      icon: ClipboardCheck,
      title: 'Review & Bill',
      description: 'AI-generated time entries appear in your dashboard, ready for review and export to your billing system. Full control, zero effort.',
      visual: 'review'
    }
  ];

  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5 mb-4">
              <span className="text-sm font-medium text-indigo-700">Simple Process</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              How TimeFlow Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get started in under 5 minutes. No complex setup, no training required.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, i) => (
            <AnimatedSection key={i} delay={i * 150}>
              <div className="relative">
                {/* Connector Line */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-indigo-200 to-transparent z-0" style={{ width: 'calc(100% - 2rem)' }} />
                )}
                
                <div className="relative bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:border-indigo-200 hover:shadow-lg transition-all group">
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg">
                    {step.number}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-6 mt-4 group-hover:scale-110 transition-transform">
                    <step.icon className="w-8 h-8 text-indigo-600" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={450}>
          <div className="text-center mt-12">
            <p className="text-slate-500 mb-6">Average setup time: under 5 minutes. No IT department required.</p>
            <button className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-4 rounded-xl transition-all hover:shadow-xl hover:shadow-indigo-500/30 inline-flex items-center gap-2">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: 'Recover Lost Revenue',
      description: 'Capture 20-40% more billable time that\'s currently slipping through the cracks.',
      proof: 'Anderson & Associates recovered $47,000 in the first quarter',
      color: 'emerald'
    },
    {
      icon: Clock,
      title: 'Eliminate Time Entry Burden',
      description: 'Give attorneys their evenings back—no more reconstructing the day\'s work.',
      proof: '"Our associates save 45 minutes daily" — Managing Partner, 50-attorney firm',
      color: 'indigo'
    },
    {
      icon: Shield,
      title: 'Improve Billing Accuracy',
      description: 'AI-generated entries include activity context, reducing client disputes.',
      proof: 'Write-offs decreased 30% after implementing TimeFlow',
      color: 'purple'
    },
    {
      icon: Plug,
      title: 'Seamless Integration',
      description: 'Works with the tools you already use—no workflow changes required.',
      proof: '18+ pre-built integrations with industry-standard platforms',
      color: 'blue'
    }
  ];

  const colorClasses = {
    emerald: { bg: 'bg-emerald-100', icon: 'text-emerald-600', accent: 'bg-emerald-600' },
    indigo: { bg: 'bg-indigo-100', icon: 'text-indigo-600', accent: 'bg-indigo-600' },
    purple: { bg: 'bg-purple-100', icon: 'text-purple-600', accent: 'bg-purple-600' },
    blue: { bg: 'bg-blue-100', icon: 'text-blue-600', accent: 'bg-blue-600' }
  };

  return (
    <section id="benefits" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-4">
              <span className="text-sm font-medium text-slate-600">Real Results</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              What TimeFlow Delivers
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Join hundreds of law firms already capturing more billable time and recovering lost revenue.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {benefits.map((benefit, i) => (
            <AnimatedSection key={i} delay={i * 100}>
              <div className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-indigo-200 hover:shadow-lg transition-all h-full">
                <div className="flex items-start gap-5">
                  <div className={`w-14 h-14 rounded-2xl ${colorClasses[benefit.color].bg} flex items-center justify-center flex-shrink-0`}>
                    <benefit.icon className={`w-7 h-7 ${colorClasses[benefit.color].icon}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                    <p className="text-slate-600 mb-4">{benefit.description}</p>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50`}>
                      <div className={`w-2 h-2 rounded-full ${colorClasses[benefit.color].accent}`} />
                      <span className="text-sm text-slate-600">{benefit.proof}</span>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Integrations Section with Logos
const IntegrationsSection = () => {
  const categories = [
    {
      name: 'Document Management',
      icon: FileText,
      integrations: [
        { name: 'iManage', desc: 'Document activity & collaboration', Logo: Logos.LogoIManage },
        { name: 'NetDocuments', desc: 'Version history & matter tracking', Logo: Logos.LogoNetDocuments },
        { name: 'Worldox', desc: 'File interactions & management', Logo: Logos.LogoWorldox }
      ]
    },
    {
      name: 'Practice Management',
      icon: Briefcase,
      integrations: [
        { name: 'Clio', desc: 'Case management & billing sync', Logo: Logos.LogoClio },
        { name: 'PracticePanther', desc: 'Workflow integration', Logo: Logos.LogoPracticePanther },
        { name: 'Smokeball', desc: 'Automatic time & matter sync', Logo: Logos.LogoSmokeball }
      ]
    },
    {
      name: 'Communication',
      icon: MessageSquare,
      integrations: [
        { name: 'Microsoft Teams', desc: 'Calls, meetings, chat logging', Logo: Logos.LogoTeams },
        { name: 'Slack', desc: 'Messages & channel activity', Logo: Logos.LogoSlack },
        { name: 'Zoom', desc: 'Meeting & call duration', Logo: Logos.LogoZoom },
        { name: 'Phone Systems', desc: 'Call logs & duration', Logo: Logos.LogoPhoneSystems }
      ]
    },
    {
      name: 'Court & Filing',
      icon: Gavel,
      integrations: [
        { name: 'PACER', desc: 'Federal docket & filing activity', Logo: Logos.LogoPACER },
        { name: 'E-Filing Systems', desc: 'CM/ECF, OneLegal, state portals', Logo: Logos.LogoEFiling }
      ]
    },
    {
      name: 'Legal Research',
      icon: BookOpen,
      integrations: [
        { name: 'Westlaw', desc: 'Research time & search activity', Logo: Logos.LogoWestlaw },
        { name: 'LexisNexis', desc: 'Sessions & Shepard\'s citations', Logo: Logos.LogoLexisNexis },
        { name: 'Calendar Systems', desc: 'Court dates & deadlines', Logo: Logos.LogoCalendar }
      ]
    },
    {
      name: 'Billing & Financial',
      icon: CreditCard,
      integrations: [
        { name: 'Elite 3E', desc: 'Billing & financials sync', Logo: Logos.LogoElite3E },
        { name: 'Aderant', desc: 'Financial management', Logo: Logos.LogoAderant },
        { name: 'Thomson Reuters', desc: 'Legal billing suites', Logo: Logos.LogoThomsonReuters }
      ]
    }
  ];

  return (
    <section id="integrations" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5 mb-4">
              <Plug className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">18+ Integrations</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Connects to Your Entire Legal Tech Stack
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Pre-built integrations with the platforms law firms trust. No custom development required.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, i) => (
            <AnimatedSection key={i} delay={i * 75}>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-indigo-200 transition-colors h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <category.icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">{category.name}</h3>
                </div>
                <div className="space-y-3">
                  {category.integrations.map((integration, j) => (
                    <div key={j} className="flex items-start gap-3 p-2 rounded-lg hover:bg-white transition-colors">
                      <integration.Logo className="w-10 h-10 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-700 text-sm block">{integration.name}</span>
                        <span className="text-slate-500 text-xs">{integration.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={500}>
          <div className="text-center mt-12">
            <p className="text-slate-500 mb-4">Don't see your tool? We're adding integrations monthly.</p>
            <button className="text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-2 transition-colors">
              Request an Integration
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Use Cases Section
const UseCasesSection = () => {
  const useCases = [
    {
      icon: Building2,
      title: 'For Managing Partners',
      pain: 'Concerned about firm-wide revenue leakage and utilization rates',
      solution: 'Get visibility into captured vs. estimated time across all attorneys. Identify coaching opportunities and revenue recovery potential.',
      result: 'See firm-wide analytics showing where billable time is being lost'
    },
    {
      icon: Users,
      title: 'For Associates',
      pain: 'Exhausted by reconstructing the day\'s work at 7 PM',
      solution: 'TimeFlow captures your document reviews, research sessions, calls, and emails automatically. Just review and approve.',
      result: 'Leave the office knowing your time is already captured'
    },
    {
      icon: Briefcase,
      title: 'For Legal Operations',
      pain: 'Struggling to get adoption of time-tracking across the firm',
      solution: 'TimeFlow requires zero behavior change—it works in the background with existing tools. Adoption happens automatically.',
      result: 'Roll out to the entire firm without training headaches'
    },
    {
      icon: UserCircle,
      title: 'For Solo Practitioners',
      pain: 'Wearing every hat means time tracking falls through the cracks',
      solution: 'Focus on client work while TimeFlow handles the administrative burden. Every email, call, and document is captured.',
      result: 'Bill for all the work you do, not just what you remember'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-br from-indigo-900 via-indigo-800 to-purple-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-300 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-sm font-medium text-indigo-200">For Every Role</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Built for Every Legal Professional
            </h2>
            <p className="text-lg text-indigo-200 max-w-2xl mx-auto">
              Whether you're managing a large firm or practicing solo, TimeFlow adapts to your needs.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 gap-6">
          {useCases.map((useCase, i) => (
            <AnimatedSection key={i} delay={i * 100}>
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 lg:p-8 hover:bg-white/15 transition-colors h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <useCase.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">{useCase.title}</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-indigo-300 mb-1">The Challenge</p>
                    <p className="text-indigo-100">{useCase.pain}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-indigo-300 mb-1">The Solution</p>
                    <p className="text-white">{useCase.solution}</p>
                  </div>
                  <div className="pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <p className="text-emerald-300 font-medium">{useCase.result}</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "TimeFlow recovered $127,000 in unbilled time our first quarter. It paid for itself in the first week.",
      author: "Sarah Chen",
      title: "Managing Partner",
      firm: "Chen & Associates LLP",
      avatar: "SC"
    },
    {
      quote: "I used to spend my evenings reconstructing time entries. Now I just review and approve what TimeFlow captures. Life-changing.",
      author: "Michael Torres",
      title: "Senior Associate",
      firm: "Whitmore Legal Group",
      avatar: "MT"
    },
    {
      quote: "Rolling out TimeFlow was the easiest software implementation we've ever done. Zero training required—it just works.",
      author: "Jennifer Park",
      title: "Director of Legal Operations",
      firm: "Sterling Partners",
      avatar: "JP"
    }
  ];

  return (
    <section id="testimonials" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-4">
              <Star className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-slate-600">4.8/5 Average Rating</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Trusted by Law Firms Like Yours
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              See what legal professionals are saying about TimeFlow.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((testimonial, i) => (
            <AnimatedSection key={i} delay={i * 100}>
              <div className="bg-white rounded-2xl p-6 lg:p-8 border border-slate-200 shadow-sm hover:shadow-lg transition-shadow h-full flex flex-col">
                <Quote className="w-10 h-10 text-indigo-200 mb-4" />
                <p className="text-slate-700 leading-relaxed mb-6 flex-1">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-sm text-slate-500">{testimonial.title}, {testimonial.firm}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Trust Badges */}
        <AnimatedSection delay={400}>
          <div className="mt-16 flex flex-wrap items-center justify-center gap-8">
            <div className="flex items-center gap-2 text-slate-500">
              <Lock className="w-5 h-5" />
              <span className="text-sm font-medium">SOC 2 Type II Certified</span>
            </div>
            <div className="flex items-center gap-2 text-slate-500">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-slate-500">
              <Award className="w-5 h-5" />
              <span className="text-sm font-medium">Best Legal Tech 2024</span>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How long does setup take?",
      answer: "Most firms are up and running in under 5 minutes. Connect your integrations, and TimeFlow starts learning immediately. No IT department required—any attorney can set it up."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. TimeFlow is SOC 2 Type II certified and uses bank-level encryption. Your data never leaves secure servers, and we never share client information. We're trusted by Am Law 100 firms."
    },
    {
      question: "Will this work with our existing billing software?",
      answer: "Yes. TimeFlow exports directly to Elite 3E, Aderant, Clio, and all major legal billing platforms. Time entries flow seamlessly into your existing workflow with one click."
    },
    {
      question: "What if TimeFlow captures time incorrectly?",
      answer: "You're always in control. TimeFlow suggests entries based on your activity, but you review and approve everything before it hits your billing system. Think of it as a smart assistant that drafts your time—you have final say."
    },
    {
      question: "Do attorneys need to change how they work?",
      answer: "Not at all. TimeFlow runs silently in the background. Attorneys keep using their existing tools—iManage, Westlaw, Teams, everything. The only change is having accurate time entries waiting for review."
    },
    {
      question: "What happens after the free trial?",
      answer: "Choose the plan that fits your firm, or continue on our free tier with limited features. No credit card required to start, and you can cancel anytime. No long-term contracts."
    },
    {
      question: "Can I see a demo before signing up?",
      answer: "Absolutely. Book a personalized demo with our team where we'll show TimeFlow working with your specific tools, or watch our 3-minute product walkthrough anytime."
    }
  ];

  return (
    <section id="faq" className="py-20 lg:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">
              Everything you need to know about TimeFlow.
            </p>
          </div>
        </AnimatedSection>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <AnimatedSection key={i} delay={i * 50}>
              <div className="border border-slate-200 rounded-xl overflow-hidden hover:border-indigo-200 transition-colors">
                <button
                  className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 bg-white hover:bg-slate-50 transition-colors"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <span className="font-medium text-slate-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${openIndex === i ? 'rotate-180' : ''}`} />
                </button>
                {openIndex === i && (
                  <div className="px-6 pb-5 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <AnimatedSection>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            Ready to Capture Every Billable Minute?
          </h2>
          <p className="text-xl text-indigo-100 mb-10 max-w-2xl mx-auto">
            Join 500+ law firms recovering lost revenue with TimeFlow. Start your free trial today—no credit card required.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <button className="group bg-white hover:bg-slate-50 text-indigo-700 font-semibold px-8 py-4 rounded-xl transition-all hover:shadow-xl inline-flex items-center justify-center gap-2">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-xl border border-white/30 transition-all inline-flex items-center justify-center gap-2">
              <Video className="w-5 h-5" />
              Schedule a Demo
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 justify-center text-indigo-200">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              No credit card required
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              14-day free trial
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              Cancel anytime
            </span>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const links = {
    Product: ['Features', 'Integrations', 'Pricing', 'Security', 'Changelog'],
    Resources: ['Documentation', 'API Reference', 'Blog', 'Case Studies', 'Webinars'],
    Company: ['About', 'Careers', 'Press', 'Contact', 'Partners'],
    Legal: ['Privacy Policy', 'Terms of Service', 'Security Policy', 'GDPR']
  };

  return (
    <footer className="bg-slate-900 text-slate-400 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-white">TimeFlow</span>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Intelligent time capture for law firms. Recover the revenue you're already earning.
            </p>
            <div className="flex gap-4">
              {['Twitter', 'LinkedIn', 'YouTube'].map((social, i) => (
                <a key={i} href="#" className="text-slate-500 hover:text-white transition-colors text-sm">
                  {social}
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {Object.entries(links).map(([category, items]) => (
            <div key={category}>
              <h4 className="text-white font-semibold mb-4">{category}</h4>
              <ul className="space-y-2">
                {items.map((item, i) => (
                  <li key={i}>
                    <a href="#" className="text-sm hover:text-white transition-colors">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            © 2024 TimeFlow, Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-sm">
              <Lock className="w-4 h-4" />
              SOC 2 Certified
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4" />
              GDPR Compliant
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
export default function TimeFlowLandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <HeroSection />
      <SocialProofBar />
      <TargetAudienceSection />
      <PainPointsSection />
      <HowItWorksSection />
      <BenefitsSection />
      <IntegrationsSection />
      <UseCasesSection />
      <TestimonialsSection />
      <FAQSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}
